import { DEFAULT_LANGUAGE, getLocale } from "@bounce/i18n"

import { LEGACY_BASE_URL, LEGACY_I18N_BASE_URL } from "./constants"
import type { GetUrlOptions } from "./urls"
import { getCanonicalUrl } from "./urls"

type ConvertLegacyUrlOptions = {
  url: string
} & Pick<GetUrlOptions, "enableI18nDomains">

const getLanguageFromUrl = (url: string) => {
  // Regex to match 2-character language codes after domain
  const languageMatch = url.match(/^https:\/\/(usebounce\.com|bounce\.com)\/([a-z]{2})(\/|$)/i)
  const extractedLanguage = languageMatch?.[2]
  const isValidExtractedLanguage = !!getLocale("language", extractedLanguage ?? "")

  // If the language extracted from the url is valid return it
  if (extractedLanguage && isValidExtractedLanguage) {
    return extractedLanguage
  }

  return DEFAULT_LANGUAGE
}

const removeDomainFromUrl = (url: string) => {
  let urlWithoutDomain = url.replace(LEGACY_BASE_URL, "")
  urlWithoutDomain = urlWithoutDomain.replace(LEGACY_I18N_BASE_URL, "")
  return urlWithoutDomain
}

const removeLanguageFromUrl = (path: string, language: string) => {
  if (path === `/${language}`) {
    return "/"
  }

  return path.replace(`/${language}/`, "/")
}

/**
 * Convert a legacy url to a new url
 * @example
 * convertLegacyUrl({ url: "https://usebounce.com/de/city/new-york", currentLanguage: "en" })
 * // Returns: "https://bounce.com/city/new-york"
 *
 * convertLegacyUrl({ url: "https://bounce.com/de/city/new-york", currentLanguage: "en" })
 * // Returns: "https://de.bounce.com/city/new-york"
 */
export const convertLegacyUrl = ({ url, enableI18nDomains }: ConvertLegacyUrlOptions) => {
  const isLegacyUrl = url.startsWith(LEGACY_BASE_URL) || url.startsWith(LEGACY_I18N_BASE_URL)
  if (!isLegacyUrl) return url

  const language = getLanguageFromUrl(url)
  const withoutDomain = removeDomainFromUrl(url)
  const pathWithoutLanguage = removeLanguageFromUrl(withoutDomain, language)
  const [path = "/", query] = pathWithoutLanguage.split("?")

  const { url: canonicalUrl } = getCanonicalUrl({
    language,
    path,
    absolute: true,
    currentLanguage: language,
    enableI18nDomains,
  })

  if (!query) return canonicalUrl

  return `${canonicalUrl}?${query}`
}
